import { Injectable } from '@angular/core';
import { MapModalData } from '../../user/map-redirect-modal/map-redirect-modal.component';
import { SubscriptionPlans } from '../../user/user/user.model';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { modalRegistry } from './modal-registry';
import { LEVEL_LAYER } from '../../map/mapbox/services/layer-store.service';
import { BreakpointObserverService } from './breakpoint-observer.service';
import { WelcomeModalComponent } from '../../map/popup-hint/welcome-modal/welcome-modal.component';
import { SubscriptionsMenuComponent } from '../../user/user/subscriptions-menu/subscriptions-menu.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  public isModalOpen = false;

  constructor(
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserverService
  ) {}

  public async openModal(text: MapModalData): Promise<void> {
    // Check if modal is already opened otherwise it could open in multiple instances
    if (!this.isModalOpen) {
      this.isModalOpen = true; // Set the status to open
      const dialogRef = this.dialog.open(
        await modalRegistry.MapRedirectModal(),
        {
          panelClass: 'custom-dialog-container',
          disableClose: text.disableClose,
          hasBackdrop: text.hasBackdrop,
          maxWidth: '620px',
          data: text,
        }
      );

      dialogRef.afterClosed().subscribe(() => {
        this.isModalOpen = false;
      });
    }
  }

  public async openHelpAndFeedbackModal(): Promise<void> {
    this.dialog.open(await modalRegistry.HelpAndFeedbackModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: `${Math.min(800, window.innerWidth - 32)}px`,
    });
  }


  public closeGenericModal(): void {
    if (this.isModalOpen) {
      this.dialog.closeAll();
      this.isModalOpen = false;
    }
  }

  public async handleSubscriptionConfirmationModal(
    plan: SubscriptionPlans,
    isPeriodMonthly: boolean,
    freePeriodString: string | null,
    promoCode: string | null
  ): Promise<void> {
    this.dialog.open(await modalRegistry.SubscriptionConfirmationModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '620px',
      data: {
        plan: plan,
        isPeriodMonthly: isPeriodMonthly,
        freePeriodString: freePeriodString,
        promoCode: promoCode,
      },
    });
  }

  public async handleSubscriptionRenewalModal(
    isAutoRenewable: boolean
  ): Promise<void> {
    this.dialog.open(await modalRegistry.SubscriptionRenewalModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '620px',
      data: {
        isAutoRenewable: isAutoRenewable,
      },
    });
  }

  public async handleFavoriteCellPersonalizationModal(
    id: string,
    title: string,
    level: LEVEL_LAYER
  ): Promise<void> {
    this.dialog.open(await modalRegistry.FavoriteCellPersonalizationModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '620px',
      maxHeight: '510px',
      data: { id, title: title, level: level },
    });
  }

  public async handleSetRadiusModal(radius: number): Promise<void> {
    this.dialog.open(await modalRegistry.SetRadiusModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '560px',
      maxHeight: '275px',
      data: { radius: radius },
    });
  }

  public async openWelcomeModal(): Promise<
    MatDialogRef<WelcomeModalComponent>
  > {
    return this.dialog.open(await modalRegistry.WelcomeModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: this.breakpointObserver.isMobile.value
        ? window.innerHeight < 500
          ? '548px'
          : '348'
        : '800px',
      maxHeight: this.breakpointObserver.isMobile.value
        ? window.innerHeight < 500
          ? `${window.innerHeight * 0.8}`
          : '594px'
        : '570px',
    });
  }

  private subscriptionsModal?: MatDialogRef<SubscriptionsMenuComponent>;

  public closeSubscriptionsModal(): void {
    this.subscriptionsModal?.close();
  }

  public async openSubscriptionsModal(mode: "all_packages" | "ai_packages"): Promise<void> {
    if (
      this.subscriptionsModal &&
      this.subscriptionsModal.getState() === MatDialogState.OPEN
    ) {
      return; //this.subscriptionsModal;
    }
    const isMobile = this.breakpointObserver.isMobile.value;

    const res = this.dialog.open(await modalRegistry.SubscriptionPlansModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: isMobile ? '800px' : '1200px',
      maxHeight: isMobile ? '1200px' : '900px',
      data: {
        mode: mode
      }
    });
    this.subscriptionsModal = res;
  }
}
